/**
 * Texte der Chat-Aktivität (Statuszeile, Gedankengang, Werkzeugschritte).
 * Deutsch ist die einzige Sprache dieser Fläche. Kein i18n-Fallback auf Englisch.
 */

export const ACTIVITY_THINKING = "Denkt nach …";
export const ACTIVITY_REASONING_SHOW = "Gedankengang anzeigen";
export const ACTIVITY_REASONING_HIDE = "Gedankengang ausblenden";
export const ACTIVITY_REASONING_TRANSLATING = "Wird übersetzt …";
export const ACTIVITY_EMPTY_ANSWER = "Die KI konnte keine Antwort liefern.";
export const ACTIVITY_STREAM_ERROR = "Unerwarteter Fehler beim Antworten.";
export const ACTIVITY_ERROR_RETRY = "Etwas ist schiefgelaufen. Bitte versuche es erneut.";
/** UI-stream error code from the chat route when a transient failure is not retried. */
export const AI_OVERLOADED_CODE = "ai_overloaded";
export const AI_OVERLOADED_MESSAGE =
  "Die KI ist gerade ausgelastet – bitte gleich nochmal senden.";
export const ACTIVITY_TOOL_ERROR = "Der Arbeitsschritt ist fehlgeschlagen.";
export const UNMAPPED_TOOL_RUNNING = "Führt einen Arbeitsschritt aus …";
export const UNMAPPED_TOOL_DONE = "Hat einen Arbeitsschritt ausgeführt";

export const ACTIVITY_TRANSLATE_INSTRUCTION =
  "Übersetze ins Deutsche. Gib nur die Übersetzung aus. Code, Bezeichner, Dateinamen und URLs unverändert lassen.";

export type ToolActivityState = "running" | "done" | "error";

type ToolActivityLabel = { running: string; done: string };

function label(running: string, done: string): ToolActivityLabel {
  return { running, done };
}

export const TOOL_ACTIVITY_LABELS: Record<string, ToolActivityLabel> = {
  search_stammdaten: label("Durchsuche Stammdaten…", "Habe die Stammdaten durchsucht"),
  list_materials: label("Lade Materialien…", "Habe die Materialien geladen"),
  list_leistungen: label("Lade Leistungen…", "Habe die Leistungen geladen"),
  get_omd_product: label("Lade Produkt…", "Habe das Produkt geladen"),
  read_omd_document: label("Lade Produktdokument…", "Habe das Produktdokument geladen"),
  get_lead: label("Lade Projektdaten…", "Habe die Projektdaten geladen"),
  list_leads: label("Lade Projekte…", "Habe die Projekte geladen"),
  get_lead_positions: label("Lade Positionen…", "Habe die Positionen geladen"),
  get_driving_distance: label("Berechne Fahrstrecke…", "Habe die Fahrstrecke berechnet"),
  list_project_offerings: label("Lade Angebote…", "Habe die Angebote geladen"),
  list_lead_documents: label("Lade Projektdokumente…", "Habe die Projektdokumente geladen"),
  search_lead_documents: label("Durchsuche Projektdokumente…", "Habe die Projektdokumente durchsucht"),
  search_instance_documents: label("Durchsuche Dokumente…", "Habe die Dokumente durchsucht"),
  read_lead_document: label("Lese Projektdokument…", "Habe das Projektdokument gelesen"),
  read_chat_document: label("Lese Chat-Dokument…", "Habe das Chat-Dokument gelesen"),
  summarize_document: label("Fasse Dokument zusammen…", "Habe das Dokument zusammengefasst"),
  request_create_offering: label("Erstelle Angebot…", "Habe das Angebot erstellt"),
  request_add_position_from_material: label("Füge Materialposition hinzu…", "Habe die Materialposition hinzugefügt"),
  request_add_position_from_leistung: label("Füge Leistungsposition hinzu…", "Habe die Leistungsposition hinzugefügt"),
  request_add_free_position: label("Füge Position hinzu…", "Habe die Position hinzugefügt"),
  request_add_free_positions: label("Füge Positionen hinzu…", "Habe die Positionen hinzugefügt"),
  request_add_offer_block: label("Füge Angebotsbaustein hinzu…", "Habe den Angebotsbaustein hinzugefügt"),
  request_reorder_offering_positions: label("Ordne Positionen neu…", "Habe die Positionen neu geordnet"),
  request_update_position: label("Aktualisiere Position…", "Habe die Position aktualisiert"),
  request_delete_position: label("Lösche Position…", "Habe die Position gelöscht"),
  request_delete_positions: label("Lösche Positionen…", "Habe die Positionen gelöscht"),
  request_apply_material_surcharge: label("Setze Materialaufschlag…", "Habe den Materialaufschlag gesetzt"),
  update_offering_document_meta: label("Aktualisiere Angebotsdaten…", "Habe die Angebotsdaten aktualisiert"),
  request_import_gaeb_positions: label("Importiere GAEB-Positionen…", "Habe die GAEB-Positionen importiert"),
  request_import_lv_pdf_positions: label("Lese Leistungsverzeichnis aus PDF…", "Habe das Leistungsverzeichnis aus dem PDF übernommen"),
  request_export_gaeb: label("Exportiere GAEB…", "Habe GAEB exportiert"),
  request_create_lead: label("Erstelle Projekt…", "Habe das Projekt erstellt"),
  request_update_lead_status: label("Ändere Projektstatus…", "Habe den Projektstatus geändert"),
  request_update_lead: label("Aktualisiere Projektdaten…", "Habe die Projektdaten aktualisiert"),
  list_lead_notes: label("Lade Projektnotizen…", "Habe die Projektnotizen geladen"),
  request_update_site_description: label("Aktualisiere Baustellenbeschreibung…", "Habe die Baustellenbeschreibung aktualisiert"),
  request_add_lead_note: label("Füge Projektnotiz hinzu…", "Habe die Projektnotiz hinzugefügt"),
  request_delete_lead_note: label("Lösche Projektnotiz…", "Habe die Projektnotiz gelöscht"),
  list_lead_activities: label("Lade Aktivitäten…", "Habe die Aktivitäten geladen"),
  list_activities: label("Lade Termine…", "Habe die Termine geladen"),
  suggest_activity_slots: label("Suche Terminslots…", "Habe Terminslots gesucht"),
  request_create_activity: label("Lege Aktivität an…", "Habe die Aktivität angelegt"),
  request_update_activity: label("Aktualisiere Aktivität…", "Habe die Aktivität aktualisiert"),
  request_delete_activity: label("Lösche Termine…", "Habe die Termine gelöscht"),
  request_import_employee_timesheet: label("Importiere Stundenzettel…", "Habe den Stundenzettel importiert"),
  list_employee_time_entries: label("Lade Zeiterfassung…", "Habe die Zeiterfassung geladen"),
  request_create_plantafel_absence: label("Trage Abwesenheit ein…", "Habe die Abwesenheit eingetragen"),
  request_delete_plantafel_absence: label("Lösche Abwesenheit…", "Habe die Abwesenheit gelöscht"),
  request_update_plantafel_absence: label("Aktualisiere die Abwesenheit…", "Habe die Abwesenheit aktualisiert"),
  list_plantafel_absences: label("Lade Abwesenheiten…", "Habe die Abwesenheiten geladen"),
  list_employees: label("Lade Mitarbeiter…", "Habe die Mitarbeiter geladen"),
  get_employee: label("Lade Mitarbeiter…", "Habe den Mitarbeiter geladen"),
  request_create_employee: label("Lege Mitarbeiter an…", "Habe den Mitarbeiter angelegt"),
  request_update_employee: label("Aktualisiere Mitarbeiter…", "Habe den Mitarbeiter aktualisiert"),
  request_delete_employee: label("Lösche den Mitarbeiter…", "Habe den Mitarbeiter gelöscht"),
  request_upsert_employee_time_entry: label("Speichere den Zeiteintrag…", "Habe den Zeiteintrag gespeichert"),
  request_delete_employee_time_entry: label("Lösche den Zeiteintrag…", "Habe den Zeiteintrag gelöscht"),
  request_create_contact: label("Lege Kontakt an…", "Habe den Kontakt angelegt"),
  request_update_contact: label("Aktualisiere Kontakt…", "Habe den Kontakt aktualisiert"),
  request_delete_contact: label("Lösche den Kontakt…", "Habe den Kontakt gelöscht"),
  get_contact: label("Lade Kontakt…", "Habe den Kontakt geladen"),
  list_contacts: label("Lade Kontakte…", "Habe die Kontakte geladen"),
  request_update_organization: label("Aktualisiere Organisation…", "Habe die Organisation aktualisiert"),
  request_delete_organization: label("Lösche die Organisation…", "Habe die Organisation gelöscht"),
  get_organization: label("Lade Organisation…", "Habe die Organisation geladen"),
  find_organization: label("Suche Organisation…", "Habe die Organisation gesucht"),
  create_organization: label("Lege Organisation an…", "Habe die Organisation angelegt"),
  list_organizations: label("Lade Organisationen…", "Habe die Organisationen geladen"),
  fetch_knowledge: label("Lade Arbeitsanweisung…", "Habe die Arbeitsanweisung geladen"),
  fetch_webpage: label("Lade Webseite…", "Habe die Webseite geladen"),
  web_search: label("Durchsuche das Web…", "Habe das Web durchsucht"),
  web_research: label("Durchsuche das Web…", "Habe das Web durchsucht"),
  remember_fact: label("Speichere Merkhilfe…", "Habe die Merkhilfe gespeichert"),
  list_invoices: label("Lade Rechnungen…", "Habe die Rechnungen geladen"),
  get_invoice: label("Lade Rechnung…", "Habe die Rechnung geladen"),
  get_offering_billing_ledger: label("Lade Abrechnungsstand…", "Habe den Abrechnungsstand geladen"),
  request_configure_outgoing_invoice_draft: label("Bereite Rechnungsentwurf vor…", "Habe den Rechnungsentwurf vorbereitet"),
  request_mark_invoice_paid: label("Markiere die Rechnung als bezahlt…", "Habe die Rechnung als bezahlt markiert"),
  request_upload_incoming_invoice: label("Lade die Eingangsrechnung hoch…", "Habe die Eingangsrechnung hochgeladen"),
  request_update_incoming_invoice: label("Aktualisiere die Eingangsrechnung…", "Habe die Eingangsrechnung aktualisiert"),
  list_emails: label("Lade E-Mails…", "Habe die E-Mails geladen"),
  search_crm_emails: label("Durchsuche E-Mails…", "Habe die E-Mails durchsucht"),
  search_email_hits: label("Durchsuche E-Mails…", "Habe die E-Mails durchsucht"),
  read_email: label("Lade E-Mail…", "Habe die E-Mail geladen"),
  get_email: label("Lade E-Mail…", "Habe die E-Mail geladen"),
  read_email_attachment: label("Lese E-Mail-Anhang…", "Habe den E-Mail-Anhang gelesen"),
  email_research: label("Durchsuche E-Mails…", "Habe die E-Mails durchsucht"),
  document_research: label("Lese Dokument…", "Habe das Dokument gelesen"),
  search_historic_offers: label("Durchsuche historische Angebote…", "Habe historische Angebote durchsucht"),
  search_offer_history: label("Durchsuche eigene Angebote…", "Habe eigene Angebote durchsucht"),
  list_supplier_catalogs: label("Lade Großhändler-Kataloge…", "Habe die Großhändler-Kataloge geladen"),
  compare_supplier_prices: label("Vergleiche Großhändlerpreise…", "Habe die Großhändlerpreise verglichen"),
  get_metal_quotes: label("Lade Metallnotierungen…", "Habe die Metallnotierungen geladen"),
  request_recalculate_metal_surcharge: label("Berechne den Metallzuschlag neu…", "Habe den Metallzuschlag neu berechnet"),
  get_ids_cart_preview: label("Prüfe den IDS-Warenkorb…", "Habe den IDS-Warenkorb geprüft"),
  request_send_mail: label("Sende E-Mail…", "Habe die E-Mail gesendet"),
  request_send_mail_batch: label("Sende E-Mails…", "Habe die E-Mails gesendet"),
  request_send_offering_document: label("Sende das Angebotsdokument…", "Habe das Angebotsdokument gesendet"),
  request_send_dunning: label("Sende die Mahnung…", "Habe die Mahnung gesendet"),
  request_reply_inbox_email: label("Sende Antwort…", "Habe die Antwort gesendet"),
  request_star_inbox_email: label("Setze Markierung…", "Habe die Markierung gesetzt"),
  request_set_inbox_email_status: label("Setze Status…", "Habe den Status gesetzt"),
  request_assign_inbox_email: label("Weise E-Mail zu…", "Habe die E-Mail zugewiesen"),
  request_set_inbox_email_category: label("Setze Kategorie…", "Habe die Kategorie gesetzt"),
  request_archive_inbox_email: label("Archiviere die E-Mail…", "Habe die E-Mail archiviert"),
  request_link_inbox_email: label("Verknüpfe die E-Mail…", "Habe die E-Mail verknüpft"),
  list_inbox_categories: label("Lese Kategorien…", "Habe die Kategorien gelesen"),
  request_create_inbox_category: label("Lege Kategorie an…", "Habe die Kategorie angelegt"),
  request_bulk_set_inbox_email_category: label("Sortiere Mails ein…", "Habe die Mails einsortiert"),
  request_update_inbox_category: label("Ändere Kategorie…", "Habe die Kategorie geändert"),
  request_delete_inbox_category: label("Lösche Kategorie…", "Habe die Kategorie gelöscht"),
  attach_file_to_lead: label("Hänge Datei ans Projekt…", "Habe die Datei ans Projekt gehängt"),
  attach_file_to_employee: label("Hänge Datei an Mitarbeiter…", "Habe die Datei an den Mitarbeiter gehängt"),
  add_lead_document_to_offer_attachments: label("Hänge Dokument ans Angebot…", "Habe das Dokument ans Angebot gehängt"),
  get_stats: label("Lade Kennzahlen…", "Habe die Kennzahlen geladen"),
  list_ausschreibungen: label("Lade Ausschreibungen…", "Habe die Ausschreibungen geladen"),
  get_ausschreibung: label("Lade Ausschreibung…", "Habe die Ausschreibung geladen"),
  list_checklists: label("Lade Checklisten…", "Habe die Checklisten geladen"),
  get_checklist: label("Lade Checkliste…", "Habe die Checkliste geladen"),
  request_create_checklist: label("Erstelle Checkliste…", "Habe die Checkliste erstellt"),
  request_update_checklist: label("Aktualisiere Checkliste…", "Habe die Checkliste aktualisiert"),
  request_delete_checklist: label("Lösche Checkliste…", "Habe die Checkliste gelöscht"),
  list_todos: label("Lade To-dos…", "Habe die To-dos geladen"),
  create_todos: label("Lege To-dos an…", "Habe die To-dos angelegt"),
  update_todos: label("Aktualisiere To-dos…", "Habe die To-dos aktualisiert"),
  delete_todos: label("Lösche To-dos…", "Habe die To-dos gelöscht"),
  list_fahrzeuge: label("Lade Fahrzeuge…", "Habe die Fahrzeuge geladen"),
  get_fahrzeug: label("Lade Fahrzeug…", "Habe das Fahrzeug geladen"),
  request_create_fahrzeug: label("Lege das Fahrzeug an…", "Habe das Fahrzeug angelegt"),
  request_update_fahrzeug: label("Aktualisiere das Fahrzeug…", "Habe das Fahrzeug aktualisiert"),
  request_delete_fahrzeug: label("Lösche das Fahrzeug…", "Habe das Fahrzeug gelöscht"),
  request_create_fahrzeug_erinnerung: label("Lege die Fahrzeug-Erinnerung an…", "Habe die Fahrzeug-Erinnerung angelegt"),
  request_update_fahrzeug_erinnerung: label("Aktualisiere die Fahrzeug-Erinnerung…", "Habe die Fahrzeug-Erinnerung aktualisiert"),
  request_delete_fahrzeug_erinnerung: label("Lösche die Fahrzeug-Erinnerung…", "Habe die Fahrzeug-Erinnerung gelöscht"),
  request_create_fahrzeug_reparatur: label("Lege die Reparatur an…", "Habe die Reparatur angelegt"),
  request_update_fahrzeug_reparatur: label("Aktualisiere die Reparatur…", "Habe die Reparatur aktualisiert"),
  request_delete_fahrzeug_reparatur: label("Lösche die Reparatur…", "Habe die Reparatur gelöscht"),
  request_create_fahrzeug_tankung: label("Erfasse die Tankung…", "Habe die Tankung erfasst"),
  request_update_fahrzeug_tankung: label("Aktualisiere die Tankung…", "Habe die Tankung aktualisiert"),
  request_delete_fahrzeug_tankung: label("Lösche die Tankung…", "Habe die Tankung gelöscht"),
  request_attach_fahrzeug_dokument: label("Hänge das Fahrzeugdokument an…", "Habe das Fahrzeugdokument angehängt"),
  request_delete_fahrzeug_dokument: label("Lösche das Fahrzeugdokument…", "Habe das Fahrzeugdokument gelöscht"),
  request_attach_fahrzeug_foto: label("Hänge das Fahrzeugfoto an…", "Habe das Fahrzeugfoto angehängt"),
  request_delete_fahrzeug_foto: label("Lösche das Fahrzeugfoto…", "Habe das Fahrzeugfoto gelöscht"),
  request_complete_fahrzeug_wartung: label("Schließe die Wartung ab…", "Habe die Wartung abgeschlossen"),
  get_company_settings: label("Lade Unternehmen…", "Habe die Unternehmensdaten geladen"),
  request_update_company_settings: label("Aktualisiere die Firmendaten…", "Habe die Firmendaten aktualisiert"),
  list_project_setup: label("Lade Projekteinstellungen…", "Habe die Projekteinstellungen geladen"),
  list_recycle_bin: label("Lade Papierkorb…", "Habe den Papierkorb geladen"),
  request_restore_recycle_bin_item: label("Stelle den Eintrag wieder her…", "Habe den Eintrag wiederhergestellt"),
  request_delete_lead: label("Lösche Projekt…", "Habe das Projekt gelöscht"),
  request_delete_offering: label("Lösche den Vorgang…", "Habe den Vorgang gelöscht"),
  list_offering_vorlagen: label("Lade Angebotsvorlagen…", "Habe die Angebotsvorlagen geladen"),
  get_offering_vorlage: label("Lade die Angebotsvorlage…", "Habe die Angebotsvorlage geladen"),
  request_apply_offering_vorlage: label("Wende die Angebotsvorlage an…", "Habe die Angebotsvorlage angewendet"),
  list_templates: label("Lade Vorlagen…", "Habe die Vorlagen geladen"),
  get_template: label("Lade die Vorlage…", "Habe die Vorlage geladen"),
  request_update_template: label("Aktualisiere die Vorlage…", "Habe die Vorlage aktualisiert"),
  request_delete_template: label("Lösche die Vorlage…", "Habe die Vorlage gelöscht"),
  upload_file_as_template: label("Speichere die Datei als Vorlage…", "Habe die Datei als Vorlage gespeichert"),
  request_create_material: label("Lege das Material an…", "Habe das Material angelegt"),
  request_update_material: label("Aktualisiere das Material…", "Habe das Material aktualisiert"),
  request_delete_material: label("Lösche das Material…", "Habe das Material gelöscht"),
  request_create_leistung: label("Lege die Leistung an…", "Habe die Leistung angelegt"),
  request_update_leistung: label("Aktualisiere die Leistung…", "Habe die Leistung aktualisiert"),
  request_delete_leistung: label("Lösche die Leistung…", "Habe die Leistung gelöscht"),
  api_get: label("Lade API-Daten…", "Habe die API-Daten geladen"),
  request_api_mutation: label("Schreibe über die API…", "Habe über die API geschrieben"),
  execute_now: label("Führe die Aufgabe aus…", "Habe die Aufgabe ausgeführt"),
  create_scheduled_job: label("Lege eine geplante Aufgabe an…", "Habe die geplante Aufgabe angelegt"),
  roles_list_presets: label("Lade die Rollen…", "Habe die Rollen geladen"),
  roles_get_preset: label("Lade die Rolle…", "Habe die Rolle geladen"),
  roles_create_preset: label("Lege die Rolle an…", "Habe die Rolle angelegt"),
  roles_update_preset: label("Aktualisiere die Rolle…", "Habe die Rolle aktualisiert"),
  roles_delete_preset: label("Lösche die Rolle…", "Habe die Rolle gelöscht"),
  roles_list_members: label("Lade die Mitglieder…", "Habe die Mitglieder geladen"),
  roles_invite_member: label("Sende die Einladung…", "Habe die Einladung gesendet"),
  roles_update_member_rights: label("Aktualisiere die Rechte…", "Habe die Rechte aktualisiert"),
  tool_search: label("Suche das passende Werkzeug…", "Habe das passende Werkzeug gesucht"),
};

const warnedUnmappedTools = new Set<string>();

function shouldWarnUnmappedTool(): boolean {
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") return false;
  if (import.meta.env?.PROD === true) return false;
  return true;
}

function warnUnmappedTool(name: string): void {
  if (warnedUnmappedTools.has(name)) return;
  warnedUnmappedTools.add(name);
  if (!shouldWarnUnmappedTool()) return;
  console.warn(`[ai-activity] Kein deutsches Label für das Tool „${name}“.`);
}

export function hasMappedToolActivityLabel(toolName: string): boolean {
  return Object.prototype.hasOwnProperty.call(TOOL_ACTIVITY_LABELS, toolName);
}

export function toolProgressLabel(toolName: string, state: ToolActivityState = "running"): string {
  const name = String(toolName ?? "").trim();
  if (!name) return "Arbeite…";
  if (state === "error") return ACTIVITY_TOOL_ERROR;
  const curated = TOOL_ACTIVITY_LABELS[name];
  if (!curated) {
    warnUnmappedTool(name);
    return state === "done" ? UNMAPPED_TOOL_DONE : UNMAPPED_TOOL_RUNNING;
  }
  return state === "done" ? curated.done : curated.running;
}
